// Pronunciation / typo-report feedback — hover card + modal.
//
// This controller serves two roles in one file:
//
//   1. Hover card  — attached to each Latin transliteration span in search results.
//      Shows "Correct" (direct submit) and "Suggest edit" (opens the modal).
//
//   2. Report modal — attached once to the shared modal element in _common_modals.
//      Handles the Correct / Incorrect / Comment form and submission.
//
// The hover card dispatches a custom DOM event to open the modal.
// The same event is fired by image_context_view_controller for Ottoman typo reports.

import ApplicationController from "./application_controller"

export const FEEDBACK_ON = { ai_pronunciation: "ai_pronunciation", ottoman_typo: "ottoman_typo" }

const OPEN_MODAL_EVENT = "ai-pronunciation-feedback:open"

// Keep at most one hover card visible at a time (module-level, like Bootstrap tooltips).
let activeCard = null
let hideTimer  = null

export default class extends ApplicationController {
  static targets = [
    // — Hover card targets (per-row instances) —
    "card",
    "trigger",

    // — Modal targets (single page-level instance) —
    "modal",
    "feedbackForm",
    "feedbackSuccessView",
    "successIcon",
    "modalTitle",
    "wordTitleBox",
    "wordTitleOttoman",
    "wordTitleLatin",
    "wordCompareBox",
    "wordCompareOttoman",
    "wordCompareLatin",
    "feedbackOptionsRow",
    "feedbackMessage",
    "submissionError",
    "submitFeedbackButton"
  ]

  static values = {
    feedbackUrl:          String,  // POST endpoint (used by both hover card and modal)
    errorMessage:         String,  // Flash message text on submission failure
    placeholderCorrect:   String,  // Modal textarea placeholder per feedback type
    placeholderIncorrect: String,
    placeholderNeutral:   String,
    placeholderTypo:      String,
    modalTitleDefault:    String,
  }

  connect() {
    this.currentFeedback   = null
    this.activePlaceholder = ""
    this.closeSuccessTimer = null

    if (this.hasModalTarget) {
      // Modal instance: listen for the open event dispatched by hover cards and context view.
      this.onOpenEvent = (e) => this.openFromDetail(e.detail || {})
      document.addEventListener(OPEN_MODAL_EVENT, this.onOpenEvent)
    }

    // Touch screens don't fire mouseenter — let a tap toggle the card instead.
    if (this.hasCardTarget && window.matchMedia("(hover: none)").matches) {
      this.onTouchToggle = (e) => {
        e.stopPropagation()
        this.cardTarget.classList.contains("is-visible") ? this.hideCard() : this.showCard()
      }
      this.triggerTarget?.addEventListener("click", this.onTouchToggle)
    }
  }

  disconnect() {
    document.removeEventListener(OPEN_MODAL_EVENT, this.onOpenEvent)
    this.triggerTarget?.removeEventListener("click", this.onTouchToggle)
    if (activeCard === this) activeCard = null
    clearTimeout(this.closeSuccessTimer)
  }

  // ─── Hover card ─────────────────────────────────────────────────────────────

  showCard() {
    if (!this.hasCardTarget) return
    clearTimeout(hideTimer)
    if (activeCard && activeCard !== this) activeCard.cardTarget.classList.remove("is-visible")
    activeCard = this
    this.cardTarget.classList.add("is-visible")
  }

  scheduleHide() {
    if (!this.hasCardTarget) return
    clearTimeout(hideTimer)
    hideTimer = setTimeout(() => this.hideCard(), 120)
  }

  keepCardOpen() { clearTimeout(hideTimer) }

  hideCard() {
    this.cardTarget?.classList.remove("is-visible")
    if (activeCard === this) activeCard = null
  }

  // "Correct" button on the hover card: submit feedback directly without opening the modal.
  markCorrect(event) {
    event.preventDefault()
    event.stopPropagation()

    const btn = event.currentTarget
    if (btn.classList.contains("is-selected") || btn.disabled) return

    const spellingId = this.element.dataset.ottomanSpellingId
    if (!spellingId) return

    btn.disabled = true
    const payload = {
      ottoman_spelling_id: spellingId,
      feedback_on:         FEEDBACK_ON.ai_pronunciation,
      feedback_type:       "correct",
      ai_latin_value:      this.element.dataset.latinValue || "",
    }
    if (this.element.dataset.entrySpellingId) payload.entry_spelling_id = this.element.dataset.entrySpellingId

    this.#post(payload)
      .done(() => btn.classList.add("is-selected"))
      .fail(() => { if (this.hasErrorMessageValue) showFlashMessage(this.errorMessageValue, "error", "float") })
      .always(() => { btn.disabled = false })
  }

  // "Suggest edit" button on the hover card: open the modal with Incorrect pre-selected.
  openSuggestEdit(event) {
    event.preventDefault()
    event.stopPropagation()

    document.dispatchEvent(new CustomEvent(OPEN_MODAL_EVENT, {
      detail: {
        ottomanValue:    this.element.dataset.ottomanValue || "",
        latinValue:      this.element.dataset.latinValue || "",
        spellingId:      this.element.dataset.ottomanSpellingId || "",
        entrySpellingId: this.element.dataset.entrySpellingId || "",
        suggestEditMode: true,
      },
    }))

    this.hideCard()
  }

  // ─── Modal ──────────────────────────────────────────────────────────────────

  // Triggered by OPEN_MODAL_EVENT from hover card (Latin feedback) or context view (Ottoman typo).
  openFromDetail({
    feedbackOn      = FEEDBACK_ON.ai_pronunciation,
    feedbackType    = "",
    ottomanValue    = "",
    latinValue      = "",
    spellingId      = "",
    entrySpellingId = "",
    suggestEditMode = false,
  } = {}) {
    if (!spellingId) return
    if (feedbackOn === FEEDBACK_ON.ottoman_typo   && !ottomanValue) return
    if (feedbackOn === FEEDBACK_ON.ai_pronunciation && !ottomanValue && !latinValue) return

    const isTypo       = feedbackOn === FEEDBACK_ON.ottoman_typo
    const displayLatin = isTypo ? "" : latinValue
    const resolvedType = suggestEditMode ? "incorrect" : (feedbackType || "correct")

    this.currentFeedback = { feedbackOn, feedbackType: resolvedType, latinValue: displayLatin, ottomanValue, spellingId, entrySpellingId }
    this.#resetForm()

    this.wordCompareBoxTarget.classList.toggle("word-compare--single", isTypo || !ottomanValue || !displayLatin)
    this.wordCompareLatinTarget.classList.toggle("d-none", isTypo)
    this.feedbackOptionsRowTarget.classList.toggle("d-none", isTypo)

    if (isTypo) {
      // Ottoman typo reports skip the Correct/Incorrect/Comment tabs entirely, so populate
      // the gray word-compare box directly instead of going through #applyFeedbackType.
      this.wordCompareOttomanTarget.textContent = ottomanValue
      this.wordCompareLatinTarget.textContent   = displayLatin
      this.wordCompareBoxTarget.classList.toggle("d-none", !ottomanValue)
      this.modalTitleTarget.textContent         = this.modalTitleDefaultValue
      this.feedbackMessageTarget.placeholder    = this.activePlaceholder = this.placeholderTypoValue
    } else {
      this.#applyFeedbackType(resolvedType)
    }

    this.feedbackMessageTarget.value = ""
    this.#bsModal()?.show()
    window.feather?.replace()
  }

  // Correct / Incorrect / Comment tabs inside the modal.
  selectFeedbackType(event) {
    const type = event.currentTarget.dataset.feedbackType
    if (!type || !this.currentFeedback) return
    this.currentFeedback.feedbackType = type
    this.#applyFeedbackType(type)
  }

  submitFeedback(event) {
    event.preventDefault()
    if (!this.currentFeedback) return

    const { feedbackOn, feedbackType, latinValue, spellingId, entrySpellingId } = this.currentFeedback
    if (!spellingId || (feedbackOn === FEEDBACK_ON.ai_pronunciation && !feedbackType)) {
      this.#showError()
      return
    }

    const btn = this.submitFeedbackButtonTarget
    btn.disabled = true

    const payload = {
      ottoman_spelling_id: spellingId,
      feedback_on:         feedbackOn,
      message:             this.feedbackMessageTarget.value.trim(),
    }
    if (entrySpellingId)                        payload.entry_spelling_id = entrySpellingId
    if (feedbackOn === FEEDBACK_ON.ai_pronunciation) {
      payload.feedback_type  = feedbackType
      payload.ai_latin_value = latinValue || ""
    }

    this.#post(payload)
      .done(() => this.#showSuccess())
      .fail(() => this.#showError())
      .always(() => { btn.disabled = false })
  }

  // Smiley animation on Close, then hide the modal after 800 ms.
  closeSuccessView(event) {
    event.preventDefault()
    if (this.hasSuccessIconTarget) this.successIconTarget.classList.add("is-morphing")
    clearTimeout(this.closeSuccessTimer)
    this.closeSuccessTimer = setTimeout(() => this.#bsModal()?.hide(), 800)
  }

  onModalShown()  { this.#applyBackdrop() }
  onModalHidden() { clearTimeout(this.closeSuccessTimer); this.#resetForm(); this.currentFeedback = null }

  clearFeedbackPlaceholder()   { this.feedbackMessageTarget.placeholder = "" }
  restoreFeedbackPlaceholder() {
    if (!this.feedbackMessageTarget.value) this.feedbackMessageTarget.placeholder = this.activePlaceholder
  }

  // ─── Private ────────────────────────────────────────────────────────────────

  // Highlight the active choice button and update header + textarea placeholder.
  #applyFeedbackType(type) {
    this.feedbackOptionsRowTarget.querySelectorAll(".ai-pronunciation-feedback-choice-btn")
      .forEach(btn => btn.classList.toggle("is-selected", btn.dataset.feedbackType === type))

    // Incorrect: show only the word-pair header (Ottoman | Latin).
    // Correct / Comment: show only the plain title + gray word-compare box.
    // The two are mutually exclusive so the modal never renders both at once.
    const { ottomanValue, latinValue } = this.currentFeedback
    const useWordTitle = type === "incorrect" && ottomanValue && latinValue

    this.modalTitleTarget.classList.toggle("d-none", useWordTitle)
    this.wordTitleBoxTarget.classList.toggle("d-none", !useWordTitle)
    this.wordCompareBoxTarget.classList.toggle("d-none", useWordTitle)

    if (useWordTitle) {
      this.wordTitleOttomanTarget.textContent = ottomanValue
      this.wordTitleLatinTarget.textContent   = latinValue
    } else {
      this.modalTitleTarget.textContent         = this.modalTitleDefaultValue
      this.wordCompareOttomanTarget.textContent = ottomanValue
      this.wordCompareLatinTarget.textContent   = latinValue
    }

    const placeholders = { correct: this.placeholderCorrectValue, incorrect: this.placeholderIncorrectValue, neutral: this.placeholderNeutralValue }
    this.feedbackMessageTarget.placeholder = this.activePlaceholder = placeholders[type] || ""
  }

  #resetForm() {
    this.feedbackFormTarget.style.display        = "block"
    this.feedbackSuccessViewTarget.style.display = "none"
    this.feedbackMessageTarget.value             = ""
    this.submitFeedbackButtonTarget.disabled     = false
    this.submissionErrorTarget.classList.add("d-none")
    this.modalTitleTarget.classList.remove("d-none")
    this.wordTitleBoxTarget.classList.add("d-none")
    // Word-compare box starts hidden; openFromDetail/#applyFeedbackType reveal it once the
    // feedback type (or typo report) is known, so it never flashes alongside wordTitleBox.
    this.wordCompareBoxTarget.classList.add("d-none")
    this.feedbackOptionsRowTarget.querySelectorAll(".ai-pronunciation-feedback-choice-btn")
      .forEach(btn => btn.classList.remove("is-selected"))
    if (this.hasSuccessIconTarget) this.successIconTarget.classList.remove("is-morphing")
  }

  #showSuccess() {
    this.feedbackFormTarget.style.display        = "none"
    this.feedbackSuccessViewTarget.style.display = "flex"
  }

  #showError() {
    this.submissionErrorTarget.textContent = this.errorMessageValue
    this.submissionErrorTarget.classList.remove("d-none")
  }

  #post(payload) {
    return $.ajax({
      url:      this.feedbackUrlValue,
      type:     "POST",
      dataType: "json",
      data:     payload,
      headers:  { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "" },
    })
  }

  #bsModal() {
    return window.bootstrap?.Modal?.getOrCreateInstance(this.modalTarget)
  }

  // Style the Bootstrap backdrop: frosted dark overlay matching the mock design.
  // opacity: 1 is required — Bootstrap's .show class sets opacity 0.5, which blocks backdrop-filter.
  #applyBackdrop() {
    const backdrops = document.querySelectorAll(".modal-backdrop")
    if (!backdrops.length) return

    const backdrop = backdrops[backdrops.length - 1]
    backdrop.classList.add("ai-pronunciation-feedback-modal-backdrop")

    // When context-image-modal is already open, stack this backdrop above it (see z-index comment in search_page.scss).
    if (document.getElementById("context-image-modal")?.classList.contains("show")) {
      backdrop.style.zIndex = "500001"
    }
  }
}
